import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext(null);

// Direct fetch for profile to avoid Supabase client blocking issues
async function fetchProfileDirect(userId, accessToken) {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=*`;
  const response = await fetch(url, {
    headers: {
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/vnd.pgrst.object+json',
    },
  });

  if (response.status === 406) return null;
  if (!response.ok) throw new Error(`Failed to fetch profile: ${response.status}`);
  return response.json();
}

/**
 * Check if the user's monthly quota should be reset based on subscription period
 * For paid subscribers: reset if we're in a new billing period
 * For free trial: NEVER reset (one-time quota)
 */
async function checkAndResetMonthlyQuota(userId, accessToken, profile) {
  // Free trial users never get reset
  if (!profile || profile.subscription_tier === 'free_trial') {
    return profile;
  }

  // Only paid subscribers (pro, advanced, enterprise) get monthly reset
  const paidTiers = ['pro', 'advanced', 'enterprise'];
  if (!paidTiers.includes(profile.subscription_tier)) {
    return profile;
  }

  // Check if we have subscription period info
  // subscription_expires_at tells us when the current period ends
  const expiresAt = profile.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
  const lastReset = profile.last_quota_reset ? new Date(profile.last_quota_reset) : null;
  const now = new Date();

  // If subscription has expired, don't reset (webhook will handle tier change)
  if (expiresAt && expiresAt < now) {
    return profile;
  }

  // Calculate the start of the current billing period (1 month before expiry)
  // This handles the case where user hasn't logged in for multiple months
  if (expiresAt) {
    const periodStart = new Date(expiresAt);
    periodStart.setMonth(periodStart.getMonth() - 1);

    // Only reset if we haven't reset in this billing period
    if (!lastReset || lastReset < periodStart) {
      console.log('[Auth] Resetting monthly quota for user', userId);
      console.log('[Auth] Period start:', periodStart.toISOString(), 'Last reset:', lastReset?.toISOString());

      try {
        // Call server-side function to reset quota
        const response = await fetch('/.netlify/functions/reset-monthly-quota', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        });

        if (response.ok) {
          const updatedProfile = await response.json();
          return { ...profile, ...updatedProfile };
        }
      } catch (error) {
        console.error('[Auth] Failed to reset monthly quota:', error);
      }
    }
  }

  return profile;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load profile using direct fetch to avoid Supabase client issues
  const loadProfile = useCallback(async (userId, accessToken) => {
    try {
      let data = await fetchProfileDirect(userId, accessToken);

      if (!data) {
        // Profile not found - might be creating, retry once after delay
        await new Promise(resolve => setTimeout(resolve, 500));
        data = await fetchProfileDirect(userId, accessToken);
      }

      if (data) {
        // Check if monthly quota should be reset (for paid subscribers)
        data = await checkAndResetMonthlyQuota(userId, accessToken, data);
      }

      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      if (!initialSession?.user) {
        setLoading(false);
      }
    });

    // Listen for auth changes - only update state, don't make DB calls here
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        setSession(newSession);
        setUser(newSession?.user ?? null);
        if (!newSession?.user) {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load profile when session changes
  useEffect(() => {
    if (session?.user && session?.access_token) {
      loadProfile(session.user.id, session.access_token);
    }
  }, [session, loadProfile]);

  async function refreshProfile() {
    if (session?.user && session?.access_token) {
      await loadProfile(session.user.id, session.access_token);
    }
  }

  // Check if user has unlimited access (time-limited free account)
  const hasUnlimitedAccess = profile?.unlimited_until
    ? new Date(profile.unlimited_until) > new Date()
    : false;

  const value = {
    user,
    profile,
    loading,
    refreshProfile,
    isAuthenticated: !!user,
    isAdmin: profile?.is_admin ?? false,
    hasUnlimitedAccess,
    canMakeRequest: profile
      ? hasUnlimitedAccess || profile.requests_used < profile.requests_limit
      : false,
    requestsRemaining: hasUnlimitedAccess
      ? '∞'
      : profile
        ? profile.requests_limit - profile.requests_used
        : 0,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
